import { FeedbackCategory, ElementMetadata } from './types';
import { CATEGORY_CONFIG, MIN_DESCRIPTION_LENGTH } from './constants';

interface FeedbackFormProps {
  category: FeedbackCategory;
  description: string;
  selectedElement: ElementMetadata | null;
  isSubmitting: boolean;
  showSuccess: boolean;
  onClose: () => void;
  onCategoryChange: (category: FeedbackCategory) => void;
  onDescriptionChange: (description: string) => void;
  onStartTargeting: () => void;
  onSubmit: () => void;
}

const categoryIcons: Record<FeedbackCategory, JSX.Element> = {
  bug: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 116 0v1" />
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 014-4h4a4 4 0 014 4v3c0 3.3-2.7 6-6 6z" />
      <path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5M6 13H2M3 21c0-2.1 1.7-3.9 3.8-4M20.97 5c0 2.1-1.6 3.8-3.5 4M22 13h-4M17.2 17c2.1.1 3.8 1.9 3.8 4" />
    </svg>
  ),
  feature: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l1.912 5.813a2 2 0 001.272 1.272L21 12l-5.813 1.912a2 2 0 00-1.272 1.272L12 21l-1.912-5.813a2 2 0 00-1.272-1.272L3 12l5.813-1.912a2 2 0 001.275-1.275z" />
    </svg>
  ),
  general: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
};

const colorStyles: Record<FeedbackCategory, { base: string; active: string }> = {
  bug: {
    base: 'border-default text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10',
    active:
      'border-red-500 bg-red-50 dark:bg-red-500/25 text-red-700 dark:text-red-300 ring-2 ring-red-500',
  },
  feature: {
    base: 'border-default text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10',
    active:
      'border-blue-500 bg-blue-50 dark:bg-blue-500/25 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500',
  },
  general: {
    base: 'border-default text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10',
    active:
      'border-green-500 bg-green-50 dark:bg-green-500/25 text-green-700 dark:text-green-300 ring-2 ring-green-500',
  },
};

export function FeedbackForm({
  category,
  description,
  selectedElement,
  isSubmitting,
  showSuccess,
  onClose,
  onCategoryChange,
  onDescriptionChange,
  onStartTargeting,
  onSubmit,
}: FeedbackFormProps) {
  const descriptionLength = description.trim().length;
  const isDescriptionValid = descriptionLength >= MIN_DESCRIPTION_LENGTH;

  return (
    <div data-feedback-fab>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-blue-600 rounded-full" />
          <h2 className="text-sm font-semibold tracking-wider text-content-primary">FEEDBACK</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-content-tertiary hover:text-content-secondary rounded"
          title="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-[140px_1fr_160px] gap-4">
        {/* Left column - Category */}
        <div>
          <div className="text-[10px] font-semibold tracking-wider text-content-tertiary mb-2">
            CATEGORY
          </div>
          <div className="flex flex-col gap-1.5">
            {(
              Object.entries(CATEGORY_CONFIG) as [
                FeedbackCategory,
                (typeof CATEGORY_CONFIG)[FeedbackCategory],
              ][]
            ).map(([key, config]) => {
              const isActive = category === key;
              const styles = colorStyles[key];
              return (
                <button
                  key={key}
                  onClick={() => onCategoryChange(key)}
                  disabled={isSubmitting || showSuccess}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs font-medium transition-all ${
                    isActive ? styles.active : styles.base
                  } disabled:opacity-50`}
                  title={config.description}
                >
                  {isActive ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    categoryIcons[key]
                  )}
                  <span>{config.label}</span>
                </button>
              );
            })}
          </div>

          {showSuccess && (
            <div className="mt-3 flex items-center gap-1.5 text-green-600 text-xs font-medium">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Feedback sent!</span>
            </div>
          )}
        </div>

        {/* Middle column - Description */}
        <div>
          <div className="text-[10px] font-semibold tracking-wider text-content-tertiary mb-2">
            DESCRIPTION
          </div>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder={CATEGORY_CONFIG[category].placeholder}
            disabled={isSubmitting || showSuccess}
            rows={5}
            className="w-full resize-none rounded border border-default bg-input-bg px-3 py-2 text-sm text-content-primary placeholder:text-content-placeholder focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
          />
          <div className="mt-1 flex items-center gap-1 text-[11px] text-content-tertiary">
            <span className={!isDescriptionValid && descriptionLength > 0 ? 'text-amber-500' : ''}>
              {descriptionLength}
            </span>
            <span>/</span>
            <span>{MIN_DESCRIPTION_LENGTH} min</span>
          </div>
        </div>

        {/* Right column - Element targeting and submit */}
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold tracking-wider text-content-tertiary mb-2">
            TARGET ELEMENT
          </div>

          <button
            onClick={onStartTargeting}
            disabled={isSubmitting || showSuccess}
            className={`flex items-center gap-2 px-3 py-2 rounded border text-xs transition-colors disabled:opacity-50 ${
              selectedElement
                ? 'border-green-500/30 bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-500/20'
                : 'border-dashed border-default text-content-secondary hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10'
            }`}
          >
            {selectedElement ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            )}
            <span>{selectedElement ? 'Element Selected' : 'Select UI Element'}</span>
          </button>

          {!selectedElement && (
            <p className="mt-1.5 text-[11px] text-content-tertiary text-center">
              Click to highlight the element you&apos;re reporting about
            </p>
          )}

          {selectedElement && (
            <p className="mt-1.5 text-[11px] text-content-tertiary text-center">
              Click again to re-select
            </p>
          )}

          <div className="mt-auto pt-3">
            <button
              onClick={onSubmit}
              disabled={!isDescriptionValid || isSubmitting || showSuccess}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Sending...</span>
                </>
              ) : showSuccess ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Sent!</span>
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
